import { useMemo } from 'react'

import { CountryCode } from 'libphonenumber-js'

import {
  getCaregiverContactFromIntakeForm,
  getGuardianInfoForEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { formatPhoneNumberForDisplay } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { type Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { ContactsSidePanelData } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/clientDetailsSidePanel/contacts/types'

import { useAssignmentsData } from '../../../../assignments/data/hooks/useAssignmentsData'
import { useSelectedEpisodeForClient } from '../../../../episodes/data/hooks/useSelectedEpisodeForClient'
import { useClientAppointmentsData } from '../../data/hooks/useClientAppointmentsData'

export const useContactsSidePanelData = (): ContactsSidePanelData | undefined => {
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { data: allAppointments = [] } = useClientAppointmentsData()
  const { data: assignments } = useAssignmentsData()

  return useMemo(() => {
    const episodeAppointments = selectedEpisode?.id
      ? allAppointments.filter((a) => a.episodeId === selectedEpisode.id)
      : allAppointments

    const guardianInfo = getGuardianInfoForEpisode(episodeAppointments, selectedEpisode?.participants, {
      includeSecondaryPatientAsGuardian: selectedEpisode?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens,
    })
    const intakeCaregiverContact = getCaregiverContactFromIntakeForm(assignments, selectedEpisode?.id)

    const firstName = guardianInfo?.guardianFirstName || intakeCaregiverContact.firstName
    const lastName = guardianInfo?.guardianLastName || intakeCaregiverContact.lastName
    const phone = intakeCaregiverContact.phone
      ? formatPhoneNumberForDisplay(intakeCaregiverContact.phone, 'US')
      : guardianInfo?.guardianPhone
        ? formatPhoneNumberForDisplay(
            guardianInfo.guardianPhone,
            (guardianInfo.guardianPhoneCountryCode as CountryCode) || 'US',
          )
        : undefined
    const email = intakeCaregiverContact.email || guardianInfo?.guardianEmail
    const canLeaveVoicemail =
      intakeCaregiverContact.canLeaveVoicemail || guardianInfo?.guardianVoicemailConsent || false

    if (!firstName && !lastName && !phone && !email) {
      return undefined
    }

    return {
      guardian: { firstName, lastName, phone, email, canLeaveVoicemail },
    }
  }, [selectedEpisode, allAppointments, assignments])
}
